import { AnimatePresence, m } from 'motion/react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { describeError } from '../hooks/useAsync';
import { PAGE } from '../motion/transitions';
import { syncPendingMeals } from '../offline/sync';
import { useOnline } from '../offline/useOnline';
import { usePendingMeals } from '../offline/usePendingMeals';
import { Banner, Button, Card } from '../ui';
import { AnalysisIcon, BodyIcon, CameraIcon, HistoryIcon, TodayIcon } from '../ui/icons';

const DESTINATIONS = [
  { to: '/', icon: TodayIcon, label: 'Hoy' },
  { to: '/historial', icon: HistoryIcon, label: 'Historial' },
  { to: '/mediciones', icon: BodyIcon, label: 'Cuerpo' },
  { to: '/analisis', icon: AnalysisIcon, label: 'Análisis' },
] as const;

/**
 * Las pantallas que ocupan el foco completo esconden la barra: capturar una foto, declarar lo que
 * comiste y validar un rótulo son tareas de una sola cosa, y ofrecer cuatro salidas mientras
 * tanto es invitar a abandonarlas por la mitad.
 */
const IMMERSIVE = [
  /^\/capturar/,
  /^\/registrar/,
  // La carga rápida es la misma tarea que declarar, multiplicada: también de foco completo.
  /^\/dia$/,
  /\/rotulo$/,
  // El detalle del ítem tiene su propia barra de acción pegada al fondo. Apilarla sobre la
  // navegación dejaría dos barras compitiendo por el pulgar, y la de abajo —la que se toca sin
  // mirar— sería la equivocada.
  /^\/comidas\/[^/]+\/items\/[^/]+$/,
];

export function AppLayout() {
  const online = useOnline();
  const pending = usePendingMeals();
  const location = useLocation();
  const navigate = useNavigate();
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  /*
   * CU-12 · la cola se vacía sola al volver la conexión. Nadie debería tener que acordarse de
   * sincronizar: la comida ya la registró, el resto es problema del programa.
   */
  useEffect(() => {
    if (!online || pending.length === 0) {
      return;
    }

    let active = true;
    syncPendingMeals()
      .then((summary) => {
        if (!active || summary === null) {
          return;
        }

        const accepted = `${summary.accepted} ${summary.accepted === 1 ? 'comida' : 'comidas'}`;
        setSyncMessage(
          summary.rejected === 0
            ? `Se sincronizó ${accepted} que habías registrado sin conexión.`
            : `Se sincronizó ${accepted}. ${summary.rejected} no se pudo: ${summary.errors.join(' ')}`,
        );
      })
      .catch((error: unknown) => {
        if (active) {
          setSyncMessage(describeError(error));
        }
      });

    return () => {
      active = false;
    };
  }, [online, pending.length]);

  const immersive = IMMERSIVE.some((pattern) => pattern.test(location.pathname));

  return (
    <div className="app-shell">
      <main className={immersive ? 'app-main app-main-bare' : 'app-main'}>
        {/*
          El aviso vive acá y no en cada pantalla: RF-12 encola las comidas sin conexión, y la
          pantalla donde más importa saberlo es justamente la de registrar — que era una de las
          que no lo mostraba. Sólo Hoy lo tenía.
        */}
        {!online && (
          <Card size="tight">
            <div className="row">
              <span className="chip-dot" style={{ width: 8, height: 8 }} />
              <strong className="micro">
                Sin conexión
                {pending.length > 0 &&
                  ` · ${pending.length} ${pending.length === 1 ? 'comida encolada' : 'comidas encoladas'}`}
              </strong>
            </div>
            <span className="micro muted">
              Nada se perdió. Lo que registres se envía solo cuando vuelva la señal.
            </span>
          </Card>
        )}

        {syncMessage !== null && (
          <Banner tone="info">
            {syncMessage}{' '}
            <button
              type="button"
              className="btn btn-plain"
              onClick={() => setSyncMessage(null)}
              style={{ minHeight: 'auto', padding: 0 }}
            >
              Entendido
            </button>
          </Banner>
        )}

        {/*
          Una pantalla por vez, y la que entra espera a que la anterior termine de irse
          (`mode="wait"`). Cruzarlas superpondría dos vistas con posiciones de scroll distintas.

          La clave es el `pathname` y no la ruta con parámetros: navegar de un ítem a otro es
          cambiar de contenido, no de pantalla, y animarlo entero se sentiría como retroceder.
        */}
        <AnimatePresence mode="wait" initial={false}>
          <m.div
            key={location.pathname}
            variants={PAGE}
            initial="hidden"
            animate="visible"
            exit="gone"
            className="stack"
          >
            <Outlet />
          </m.div>
        </AnimatePresence>
      </main>

      {!immersive && (
        <nav className="bottom-nav" aria-label="Navegación principal">
          {DESTINATIONS.slice(0, 2).map((destination) => (
            <NavLink key={destination.to} to={destination.to} end={destination.to === '/'}>
              <destination.icon />
              {destination.label}
            </NavLink>
          ))}

          {/*
            La cámara no es un destino más ni un botón flotante suelto: es la acción central. Ir a
            registrar tiene que costar lo mismo desde cualquier pantalla, y el pulgar cae acá.
          */}
          <Button
            className="nav-camera"
            aria-label="Registrar una comida"
            onClick={() => navigate('/capturar')}
          >
            <CameraIcon size={26} />
          </Button>

          {DESTINATIONS.slice(2).map((destination) => (
            <NavLink key={destination.to} to={destination.to}>
              <destination.icon />
              {destination.label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
